export type LabourTime = {
  hours?: number;
  minutes?: number;
};

export type HarvestReportWorkData = {
  startDate: Date;
  hours?: number;
  minutes?: number;
  numberOfEmployees?: number;
};

export type HarvestReportResult = {
  leadTime?: number;
  labourHours?: LabourTime;
  itemsPerHour?: number;
};

function validateHarvestReportWorkData(
  workData: HarvestReportWorkData,
): boolean {
  // Check if startDate is valid (not null or invalid date)
  if (!workData.startDate || isNaN(workData.startDate.getTime())) {
    return false;
  }

  // Check if hours is greater than 0
  if (workData.hours === undefined || workData.hours === null) {
    return false;
  }

  // Check if number of employees is defined and not null
  if (workData.numberOfEmployees === undefined) {
    return false;
  }

  // If all validations pass, return null (no errors)
  return true;
}

function getTotalTime(workData: HarvestReportWorkData): LabourTime {
  const totalTimeInMinutes = workData.hours! * 60 + (workData.minutes || 0);
  const totalTimeForAllEmployees =
    totalTimeInMinutes * workData.numberOfEmployees!;

  // Convert total time to decimal hours (standard decimal format)
  const totalDecimalHours = totalTimeForAllEmployees / 60.0;

  return { hours: totalDecimalHours };
}

function calculateItemsPerHour(
  itemsCount: number,
  labourHours?: LabourTime,
): number {
  if (!labourHours?.hours || itemsCount <= 0) {
    return 0;
  }
  return parseFloat((itemsCount / labourHours.hours).toFixed(2));
}

export function calculateHarvestReportTime(
  workData: HarvestReportWorkData,
  pickupDate: Date,
  itemCount: number,
): HarvestReportResult {
  const result: HarvestReportResult = {};

  if (validateHarvestReportWorkData(workData) === false) {
    return result;
  }

  const startDateTime = isNaN(workData?.startDate?.getTime())
    ? 0
    : workData?.startDate?.getTime();
  const pickupDateTime = isNaN(pickupDate?.getTime())
    ? 0
    : pickupDate?.getTime();

  // Lead Time logic to match CSV export:
  //   1. If Pickup Date is same day or before Start Date -> 0
  //   2. Otherwise -> days difference (rounded up) + 1 to include both days
  const differenceInMs = Math.abs(startDateTime - pickupDateTime);
  const dayInMs = 1000 * 60 * 60 * 24;

  result.leadTime = Math.ceil(differenceInMs / dayInMs);
  const labourHours = getTotalTime(workData);
  result.labourHours = labourHours;
  result.itemsPerHour = calculateItemsPerHour(itemCount, labourHours);

  return result;
}
